import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatNumber, formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/requests.css';
import '../styles/requestStatusBadges.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ObservationRequestDetailModal from '../components/Observations/ObservationRequestDetailModal';
import BackHomeButton from '../components/Common/BackHomeButton';
import { TableCard } from '../components/ui';

const MySwal = withReactContent(Swal);

const ObservationPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: ''
  });

  // Estados permitidos de observación (solo Pendiente y En Proceso)
  const observationStatuses = [
    { value: 'pending', label: 'Pendiente' },
    { value: 'in_progress', label: 'En Proceso' }
  ];

  useEffect(() => {
    fetchObservations();
  }, [appliedFilters, pagination.current_page]);

  const fetchObservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: appliedFilters.search,
        status: appliedFilters.status,
        date_from: appliedFilters.from_date,
        date_to: appliedFilters.to_date,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      const response = await api.get(`/observations?${params}`);
      setRequests(response.data.observations || []);
      setPagination(response.data.pagination || {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      });
    } catch (error) {
      console.error('Error al obtener observaciones:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las observaciones',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      search: '',
      status: '',
      from_date: '',
      to_date: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const handleViewDetail = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={40} height={30} /></td>
          <td><Skeleton width={80} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td className="text-center">
            <Skeleton width={50} height={25} />
          </td>
          <td>
            <div className="d-flex gap-2">
              <Skeleton width={30} height={30} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Solicitudes Observadas</h3>
            <p className="text-subtitle text-muted">Solicitudes observadas pendientes de corrección o validación.</p>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first text-md-end mb-3 mb-md-0">
            <BackHomeButton />
          </div>
        </div>
      </div>

      <section className="section">
        <TableCard
          toolbar={
            <div className="row">
              <div className="col-md-3 mb-2">
                <label htmlFor="search" className="form-label">Buscar</label>
                <input
                  type="text"
                  id="search"
                  className="form-control"
                  placeholder="Cliente o DNI..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="from_date" className="form-label">Fecha desde</label>
                <input
                  type="date"
                  id="from_date"
                  className="form-control"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="to_date" className="form-label">Fecha hasta</label>
                <input
                  type="date"
                  id="to_date"
                  className="form-control"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-3 mb-2 d-flex align-items-end">
                <div className="w-100 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-light-primary"
                    onClick={handleApplyFilters}
                  >
                    <i className="bi bi-funnel me-1"></i>
                    Filtrar
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    onClick={handleClearFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          }
          table={
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Solicitud</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vendedor</th>
                  <th>Monto</th>
                  <th>Cuotas</th>
                  <th className="text-center">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-5">
                      <i className="bi bi-inbox rg-empty-icon-lg"></i>
                      <p className="mt-3 mb-0">No hay observaciones para mostrar</p>
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td>#{request.id}</td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            handleGoToCustomer(request.customer?.dni);
                          }}
                          className="rg-link-button"
                        >
                          {request.customer?.name || 'N/A'}
                        </button>
                        <br />
                        <small className="text-muted">
                          DNI: {request.customer?.dni || 'N/A'}
                        </small>
                      </td>
                      <td>{request.dates?.request || formatDate(request.dates?.created)}</td>
                      <td>{request.seller?.full_name || 'N/A'}</td>
                      <td>{request.amount?.formatted || formatCurrency(request.amount?.value || 0)}</td>
                      <td className="text-center">{request.installments || 'N/A'}</td>
                      <td className="text-center">
                        <span
                          className={`request-status-badge ${request.request_status?.value ? `request-status-${request.request_status.value}` : 'request-status-default'}`}
                        >
                          {request.request_status?.label || request.request_status?.value || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Ver detalle"
                            onClick={() => handleViewDetail(request)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Detalles
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination loading={loading} />}
        />

        {showDetailModal && selectedRequest && (
          <ObservationRequestDetailModal
            observationId={selectedRequest.observation_id}
            requestId={selectedRequest.id}
            customerId={selectedRequest.customer?.uid}
            request={selectedRequest}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </section>
    </div>
  );
};

export default ObservationPage;

